"use client"

import { PadManagerEvent, PadManagerEventType } from "@/interfaces/virtualPad_types"
import { PadManager } from "@/services/VirtualPad/PadManager"
import { PadSessionManager } from "@/services/VirtualPad/PadSessionManager"
import { useEffect } from "react"
import { toast } from "react-toastify"
import Notification from "../Notification/Notification"
import { useTranslations } from "next-intl"

export function VirtualPadListener() {
  const tn = useTranslations('Notifications')

  /**
   * Initialize PadSessionManager on the website and notify the server 
   * of the session closure if the page is closed unexpectedly.
   * 
   * Listens to their lifecycle events while the website is active.
   */
  useEffect(() => {
    const padSessionManager = PadSessionManager.getInstance()
    const padManager = PadManager.getInstance()

    const handlePageHide = () => {
      padSessionManager.closeSession()
    }

    const handlePageShow = (event: PageTransitionEvent) => {
      /*
       * When the page is restored from the back-forward cache,
       * JavaScript state may still be preserved.
       */
      if (event.persisted) {
        void padSessionManager.startSession()
      }
    }

    const showNotifications = (event: PadManagerEvent) => {
      switch (event.type) {
        case PadManagerEventType.VPAD_CONNECTED:
          toast.info(<Notification text={tn('vPadPaired')} />)
          break

        case PadManagerEventType.VPAD_DISCONNECTED:
          toast.info(<Notification text={tn('vPadUnpaired')} />)
          break

        case PadManagerEventType.VPAD_ERROR:
          toast.error(<Notification text={tn('vPadPairingError')} />)
          break

        case PadManagerEventType.MAX_PADS_REACHED:
          toast.warn(<Notification text={tn('vPadPairingMaxDevicesError')} />)
          break
      }
    }

    const unsubscribePadEvents = padManager.subscribe(showNotifications)

    window.addEventListener('pagehide', handlePageHide)
    window.addEventListener('pageshow', handlePageShow)

    return () => {
      unsubscribePadEvents()
      window.removeEventListener('pagehide', handlePageHide)
      window.removeEventListener('pageshow', handlePageShow)
    }
  }, [])

  return null
}